import { authService } from '@/services/authService';
import { useAuthStore } from '@/store/authStore';
import {
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  rememberMe: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    var(--primary-600) 0%,
    var(--primary-800) 100%
  );
  padding: 1rem;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  box-shadow: var(--shadow-xl);
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h1 {
    color: var(--primary-600);
    font-size: 2rem;
    font-weight: 800;
    margin-bottom: 0.5rem;
    letter-spacing: 2px;
  }

  p {
    color: var(--gray-600);
    font-size: 0.875rem;
    margin: 0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  position: relative;

  label {
    display: block;
    font-weight: 500;
    color: var(--gray-700);
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
  }

  input {
    width: 100%;
    padding: 0.625rem 0.75rem;
    padding-left: 2.5rem;
    border: 1px solid var(--gray-300);
    border-radius: 0.5rem;
    font-size: 0.875rem;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: var(--primary-600);
      box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
    }

    &::placeholder {
      color: var(--gray-400);
    }
  }
`;

const Icon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 2.25rem;
  color: var(--gray-400);
  width: 1.25rem;
  height: 1.25rem;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 2.25rem;
  background: none;
  border: none;
  color: var(--gray-400);
  cursor: pointer;
  padding: 0;
  width: 1.25rem;
  height: 1.25rem;

  &:hover {
    color: var(--gray-600);
  }

  svg {
    width: 100%;
    height: 100%;
  }
`;

const ErrorMessage = styled.p`
  color: var(--error-600);
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: var(--gray-700);
    cursor: pointer;

    input {
      width: auto;
      margin: 0;
      cursor: pointer;
    }
  }
`;

const ForgotLink = styled(Link)`
  font-size: 0.875rem;
  color: var(--primary-600);
  font-weight: 500;

  &:hover {
    color: var(--primary-700);
  }
`;

const ErrorBox = styled.div`
  background-color: var(--error-50);
  border-radius: 0.5rem;
  padding: 0.75rem;

  p {
    color: var(--error-800);
    font-size: 0.875rem;
    margin: 0;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 1rem;
  background: var(--primary-600);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RegisterLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  font-size: 0.875rem;
  color: var(--gray-600);

  a {
    color: var(--primary-600);
    font-weight: 500;

    &:hover {
      color: var(--primary-700);
    }
  }
`;

const Login: FC = () => {
  const navigate = useNavigate();
  const { setAuth, setLoading } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      rememberMe: false,
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoading(true);
      const response = await authService.login(data);

      setAuth(response.user, {
        access_token: response.access_token,
        refresh_token: response.refresh_token,
      });

      toast.success(`Welcome back, ${response.user.full_name}!`);
      navigate('/dashboard');
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { error?: string | { message?: string } } };
      };
      let errorMessage = 'Login failed. Please try again.';

      if (err.response?.data?.error) {
        if (typeof err.response.data.error === 'string') {
          errorMessage = err.response.data.error;
        } else if (
          typeof err.response.data.error === 'object' &&
          err.response.data.error.message
        ) {
          errorMessage = err.response.data.error.message;
        }
      }

      toast.error(errorMessage);
      setError('root', {
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Logo>
          <h1>MARKETEER</h1>
          <p>Commerce Platform</p>
        </Logo>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <label htmlFor="email">Email address</label>
            <Icon>
              <EnvelopeIcon />
            </Icon>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="Enter your email"
              {...register('email')}
            />
            {errors.email && (
              <ErrorMessage>{errors.email.message}</ErrorMessage>
            )}
          </FormGroup>

          <FormGroup>
            <label htmlFor="password">Password</label>
            <Icon>
              <LockClosedIcon />
            </Icon>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              {...register('password')}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
            </PasswordToggle>
            {errors.password && (
              <ErrorMessage>{errors.password.message}</ErrorMessage>
            )}
          </FormGroup>

          <CheckboxGroup>
            <label>
              <input
                id="rememberMe"
                type="checkbox"
                {...register('rememberMe')}
              />
              Remember me
            </label>

            <ForgotLink to="/forgot-password">Forgot password?</ForgotLink>
          </CheckboxGroup>

          {errors.root && (
            <ErrorBox>
              <p>{errors.root.message}</p>
            </ErrorBox>
          )}

          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </SubmitButton>
        </Form>

        <RegisterLink>
          Don't have an account? <Link to="/register">Create account</Link>
        </RegisterLink>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
